import React, { useState, useEffect, useCallback } from 'react'
import styles from '../CustomDomainModal.module.css'
import CTAButton from 'components/CTAButton/CTAButton'
import { CONN_PATH } from '../constants/strings'
import {
  getAvailableDomains,
  purchaseDomain,
  autoConnectDomainAdd,
  checkDomain,
  makeEnomStripePayment
} from 'services/custom-domain'
import { Alert, Button, Spinner, Form } from 'react-bootstrap'
import { fetchUserData } from '../../../Manage/actions'
import { useDispatch } from 'react-redux'
import { connect } from 'react-redux'
import { getCurrencySymbolByCountryCode } from 'constants/currency'
import RegistrantContactInfo from './RegistrantContactInfo'
import { addUpgradeReason, hasFeature, onUpgradePlan } from 'utils/general'
import { F_CUSTOM_DOMAIN } from 'constants/plans/constants'
import { PLANS } from 'constants/plans'
import { ERROR_CODE_MAP } from '../../../stripe.constants'

const PENDING_CUSTOM_DOMAIN_PURCHASE_KEY = 'pendingCustomDomainPurchase'

const initialValues = {
  name: '',
  price: '',
  enomPrice: null,
  status: '',
  available: false,
  payment_success: false,
  isPremium: false
}

const GetDomain = ({ formik, onPrevious, setActiveStep, onMessage, userProfile, showPayWall }) => {
  const { values } = formik
  const [loading, setLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [searchTerm, setSearchTerm] = useState({ value: '', exists: false })
  const [hasSearched, setHasSearched] = useState(false)
  const [currencySymbol, setCurrencySymbol] = useState('$')
  const [processingCountdown, setProcessingCountdown] = useState(null)
  const [showRetryMessage, setShowRetryMessage] = useState(false)
  const [pendingCountdownStart, setPendingCountdownStart] = useState(false)
  const [showContactInfo, setShowContactInfo] = useState(false)
  const dispatch = useDispatch()
  const domainSuggestions = values.path.domain_search
  const selectedDomain = values.path.selected_domain
  const isStripeCustomer = `${userProfile?.customerId}`?.startsWith('cus_')
  const currency = userProfile?.currency
  const requiresSoloForDomainPurchase = !hasFeature(userProfile?.productId, F_CUSTOM_DOMAIN)

  useEffect(() => {
    if (requiresSoloForDomainPurchase) addUpgradeReason('Purchase Custom Domain')
  }, [requiresSoloForDomainPurchase])

  const buyDomainButtonLabel = requiresSoloForDomainPurchase ? 'Upgrade plan' : 'Buy domain'
  const isBuyDomainDisabled = requiresSoloForDomainPurchase
    ? !selectedDomain.available || isLoading
    : !selectedDomain.available || isLoading || !values.path.confirmNoRefund

  useEffect(() => {
    if (userProfile?.currency) {
      const symbol = getCurrencySymbolByCountryCode(userProfile.currency)
      setCurrencySymbol(symbol)
    }
  }, [userProfile?.currency])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const isProcessing = processingCountdown !== null && processingCountdown > 0

  const handleEnomFailure = () => {
    onMessage({
      show: true,
      title: 'Domain purchase failed',
      message: 'Domain purchase failed. Please try again later.'
    })
  }

  useEffect(() => {
    if (userProfile?.payments?.length) {
      const paymentFound = userProfile.payments.some(
        (payment) => payment.domain.toLowerCase() === selectedDomain.name.toLowerCase()
      )
      if (paymentFound) {
        formik.setFieldValue('path.selected_domain.payment_success', true)
      }
    }
  }, [userProfile?.payments, selectedDomain.payment_success])

  const getDomains = async () => {
    try {
      setLoading(true)
      formik.setFieldValue('path.selected_domain', initialValues)
      const {
        data: { domains, searchedDomainAvailable }
      } = await getAvailableDomains({ domain: values.dnsAddress.domain, currency: currency?.toUpperCase() })
      const domain_search_result = domains?.length > 0 ? domains : []
      setSearchTerm({
        value: values.dnsAddress.domain,
        exists: searchedDomainAvailable
      })
      formik.setFieldValue('path.domain_search', domain_search_result)
      setHasSearched(true)
      setLoading(false)
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }

  const handleSelectDomain = useCallback(
    async (name) => {
      if (selectedDomain.name.toLowerCase() === name.toLowerCase()) {
        formik.setFieldValue('path.selected_domain.name', '')
      } else {
        formik.setFieldValue('path.selected_domain.name', name.toLowerCase())
        await checkDomainValidity(name)
      }
    },
    [selectedDomain.name]
  )

  const handleNext = () => {
    const createdTime = localStorage.getItem('start_time')
    if (!createdTime) localStorage.setItem('start_time', new Date())
    formik.setFieldValue(`dnsRecords.checked`, true)
    setActiveStep(4)
  }

  const autoAddDomain = async () => {
    try {
      const payload = {
        domain: selectedDomain.name.toLowerCase(),
        replaceWWW: true
      }
      const { data } = await autoConnectDomainAdd(payload)
      if (data?.domain) {
        formik.setFieldValue('dnsRecords.data', data)
        handleNext()
      }
      dispatch(fetchUserData(false))
    } catch (error) {
      console.error(error)
      onMessage({
        show: true,
        title: 'Ooops',
        message: `Domain purchase successful, but we encountered an error setting up your domain. Retry from your dashboard`
      })
    }
  }

  const onBuyDomain = async () => {
    const premiumPrice = selectedDomain?.isPremium ? selectedDomain.price : null
    const {
      data: { success, message }
    } = await purchaseDomain({
      domain: selectedDomain.name,
      price: premiumPrice,
      // Enom's own base price (pre-markup, USD) for every purchase, so admin oversight
      // emails can track it against Enom's actual cost rather than our derived price.
      enomPrice: selectedDomain.enomPrice,
      email: userProfile.email,
      ...values.registrantContact
    })
    if (success) {
      onMessage({
        show: true,
        title: 'Domain purchase successful',
        message: `Congratulations, you now own ${selectedDomain.name}`
      })
      await autoAddDomain()
      dispatch(fetchUserData(false))
      formik.setFieldValue('path.selected_domain', initialValues)
      return true
    } else {
      return false
    }
  }

  const checkDomainValidity = async (domain) => {
    try {
      setIsVerifying(true)
      const { data } = await checkDomain({ domain: domain.toLowerCase(), currency: currency?.toUpperCase() })
      const Code = data?.RRPCode
      const Price = data?.Price
      const isPremium = data?.isPremium
      if (Code === '210' && Price > 0) {
        const form_data = {
          name: data.Name,
          price: Price,
          enomPrice: data?.EnomPrice,
          available: true,
          status: data?.RRPText,
          payment_success: false,
          isPremium: isPremium,
          isFreeFirstYear: data?.isFreeFirstYear,
          is99CentFirstYear: data?.is99CentFirstYear,
          renewalPrice: data?.renewalPrice
        }
        formik.setFieldValue('path.selected_domain', form_data)
        setIsVerifying(false)
      } else {
        formik.setFieldValue('path.selected_domain', {
          ...initialValues,
          name: domain.toLowerCase(),
          status: data?.RRPText
        })
        setIsVerifying(false)
      }
    } catch (error) {
      setIsVerifying(false)
    }
  }

  const onPayWithStripe = async () => {
    const paywallTriggered = showPayWall()

    if (paywallTriggered) {
      if (selectedDomain?.name) {
        localStorage.setItem(PENDING_CUSTOM_DOMAIN_PURCHASE_KEY, selectedDomain.name.toLowerCase())
      }
      return
    }
    if (isStripeCustomer) {
      try {
        setIsLoading(true)

        let paymentSuccessful = selectedDomain.payment_success
        let isFreeDomainPurchased = selectedDomain.is_free_domain_purchased || selectedDomain.isFreeFirstYear

        if (!paymentSuccessful) {
          const payload = {
            customerId: userProfile.customerId,
            amount: selectedDomain.price,
            domain: selectedDomain.name,
            email: userProfile.email,
            currency: currency,
            isFreeFirstYear: selectedDomain.isFreeFirstYear,
            is99CentFirstYear: selectedDomain.is99CentFirstYear,
            renewalAmount: selectedDomain.renewalPrice
          }
          const {
            data: { success, type }
          } = await makeEnomStripePayment(payload)
          if (success) {
            paymentSuccessful = true
            formik.setFieldValue('path.selected_domain.payment_success', true)
            if (type === 'free_domain') {
              isFreeDomainPurchased = true
              formik.setFieldValue('path.selected_domain.is_free_domain_purchased', true)
            }
          }
        }

        if (paymentSuccessful) {
          try {
            if (isFreeDomainPurchased) {
              onMessage({
                show: true,
                title: 'Domain purchase successful',
                message: `Congratulations, you now own ${selectedDomain.name}`
              })
              await autoAddDomain()
              dispatch(fetchUserData(false))
              formik.setFieldValue('path.selected_domain', initialValues)
            } else {
              const domainPurchaseSuccess = await onBuyDomain()
              if (!domainPurchaseSuccess) {
                handleEnomFailure()
              }
            }
          } catch {
            handleEnomFailure()
          }
        }
        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        const declineCode = error?.response?.data?.declineCode
        const message = ERROR_CODE_MAP[declineCode] || 'Sorry, an error occurred whilst making your payment'
        onMessage({
          show: true,
          title: 'Payment Error',
          message
        })
      }
    } else {
      onMessage({
        show: true,
        title: 'Feature Not Available',
        message:
          'Sorry this feature is not currently available for your account. We recommend Godaddy.com as an alternative'
      })
    }
  }

  if (showContactInfo) {
    return (
      <RegistrantContactInfo
        userProfile={userProfile}
        onPrevious={() => setShowContactInfo(false)}
        onBuy={onPayWithStripe}
        isLoading={isLoading}
        isProcessing={isProcessing}
        formatTime={formatTime}
        processingCountdown={processingCountdown}
        showRetryMessage={showRetryMessage}
      />
    )
  }

  return (
    <>
      <div className={styles.subHeading}>
        <div className={styles.title}>{CONN_PATH.PURCHASE_TITLE}</div>
        <div className={styles.goDaddyInput}>
          <input
            className={styles.input}
            placeholder="e.g. mywebsite.com"
            type="text"
            {...formik.getFieldProps(`dnsAddress.domain`)}
          />
          <CTAButton
            label="Search for domain"
            onClick={getDomains}
            size="small"
            disabled={!formik.values.dnsAddress.domain}
            className={styles.domainSearch}
            loading={loading}
          />
        </div>

        {hasSearched && (
          <>
            {domainSuggestions.length > 0 && (
              <div className={styles.subTitle}>
                {searchTerm.exists === null
                  ? `${CONN_PATH.SUGGESTED_DOMAINS_TITLE} for "${searchTerm.value}"`
                  : `${CONN_PATH.SUGGESTED_DOMAINS_TITLE} (${searchTerm.value} ${searchTerm.exists ? 'is available' : 'is not available'})`}
              </div>
            )}
            <div className={styles.domainSearchCon}>
              {domainSuggestions.length === 0 ? (
                <div className={styles.emptyState}>
                  <img src="/assets/icons/search.svg" alt="" />
                  <div className={styles.emptyStateTitle}>No domains found for &quot;{searchTerm.value}&quot;</div>
                  <div className={styles.emptyStateSubtitle}>Try a different name, or add a TLD like .com</div>
                </div>
              ) : (
                domainSuggestions.map((domain) => {
                  const isSelected = selectedDomain.name.toLowerCase() === domain._.toLowerCase()
                  const priceChanged =
                    isSelected &&
                    domain.price != null &&
                    selectedDomain.price != null &&
                    domain.price !== selectedDomain.price
                  return (
                    <div
                      className={styles.domainSuggestion}
                      key={domain._}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectDomain(domain._)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleSelectDomain(domain._)
                        }
                      }}
                    >
                      <span className={styles.subTitle}>{domain._.toLowerCase()}</span>
                      <div className={styles.btnCon}>
                        {isSelected ? (
                          isVerifying ? (
                            <Spinner animation="border" size="sm" />
                          ) : selectedDomain.available && selectedDomain.price ? (
                            <Button variant="primary" size="sm" className={styles.btn}>
                              {selectedDomain.is99CentFirstYear
                                ? `${currencySymbol}${selectedDomain.price} for 1/yr`
                                : selectedDomain.isFreeFirstYear
                                  ? 'Free for 1/yr'
                                  : `${currencySymbol}${selectedDomain.price}`}
                              {priceChanged && <span className={styles.priceUpdatedTag}>(updated)</span>}
                            </Button>
                          ) : (
                            <span className={styles.unavailable}>Unavailable</span>
                          )
                        ) : (
                          <Button variant="outline-primary" size="sm" className={styles.btn}>
                            {domain.is99CentFirstYear
                              ? `${currencySymbol}${domain.price} for 1/yr`
                              : domain.isFreeFirstYear
                                ? 'Free for 1/yr'
                                : domain.price
                                  ? `${currencySymbol}${domain.price}`
                                  : 'Select'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            {domainSuggestions.length > 0 && !selectedDomain.name && (
              <div className={styles.priceNote}>{CONN_PATH.SUGGESTED_DOMAINS_NOTE}</div>
            )}
            {requiresSoloForDomainPurchase && (
              <Alert variant="warning" className="mt-3 mb-0">
                You will need to{' '}
                <a
                  role="button"
                  tabIndex={0}
                  className="link color-purple"
                  onClick={() => {
                    addUpgradeReason('Purchase Custom Domain')
                    onUpgradePlan({ planId: PLANS.PRO.id, userProfile }, dispatch)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      addUpgradeReason('Purchase Custom Domain')
                      onUpgradePlan({ planId: PLANS.PRO.id, userProfile }, dispatch)
                    }
                  }}
                >
                  upgrade your plan
                </a>{' '}
                first.
                <br />
                You can then purchase a custom domain later.
                <br />
                Custom domains are an additional purchase.
              </Alert>
            )}
          </>
        )}
        <>
          {!requiresSoloForDomainPurchase && selectedDomain.available && !isVerifying && (
            <div className={styles.selectedDomain}>
              {selectedDomain.status && selectedDomain.price && (
                <span className={styles.title}>{selectedDomain.status.substring(0, 53)}</span>
              )}
              <span>
                {selectedDomain.is99CentFirstYear ? (
                  <>
                    Continue to buy <u> {`${selectedDomain.name}`}</u> for{' '}
                    <strong>{`${currencySymbol}${selectedDomain.price}`}</strong> for 1/yr (renews at{' '}
                    {`${currencySymbol}${selectedDomain.renewalPrice}`}/yr).
                  </>
                ) : selectedDomain.isFreeFirstYear ? (
                  <>
                    Continue to buy <u> {`${selectedDomain.name}`}</u> for{' '}
                    <span className={styles.strikethroughPrice}>{`${currencySymbol}${selectedDomain.price}`}</span>{' '}
                    <strong>Free</strong> for 1/yr (renews at {`${currencySymbol}${selectedDomain.price}`}/year).
                  </>
                ) : (
                  <>
                    Continue to buy <u> {`${selectedDomain.name}`}</u> for {`${currencySymbol}${selectedDomain.price}`}{' '}
                    for 1/yr.
                  </>
                )}
              </span>
              <span className={styles.subTitle}>
                We will automatically charge the payment method on your account
                {selectedDomain.isFreeFirstYear || selectedDomain.is99CentFirstYear ? ' next year' : ''}.
              </span>
              <Form.Check
                type="checkbox"
                id="confirm-no-refund"
                label="I understand that once successful, no refund is available for this purchase"
                checked={values.path.confirmNoRefund}
                onChange={(e) => formik.setFieldValue('path.confirmNoRefund', e.target.checked)}
                className="mt-3"
              />
            </div>
          )}
        </>
      </div>
      <div className={styles.btnContainer}>
        <Button
          variant="outline-primary"
          onClick={onPrevious}
          size="small"
          showLeftArrow={true}
          showArrow={false}
          leftArrowClass={styles.backArrow}
        >
          Back
        </Button>
        {isProcessing ? (
          <Button size="small" className={styles.forwardButton} disabled={true}>
            Processing... {formatTime(processingCountdown)}
          </Button>
        ) : (
          <Button
            onClick={() => setShowContactInfo(true)}
            size="small"
            className={styles.forwardButton}
            disabled={
              !selectedDomain.available || (!showRetryMessage && !values.path.confirmNoRefund) || isBuyDomainDisabled
            }
          >
            Continue
          </Button>
        )}
      </div>
    </>
  )
}
const mapStateToProps = ({ manage }) => ({
  userProfile: manage.userProfile,
  showMessageModal: manage.showMessageModal
})
export default connect(mapStateToProps)(GetDomain)
